import { MathUtil } from "@base/math/MathUtil";
import { gec, ltc } from "@base/util/Comma";
import { SST } from "@biz/definitions/SST";
import { ISunSailData, MaterialGroup, PoleIndex, PoleSwordType, PoleType, SunSailType } from "@biz/definitions/SunSailDefinitions";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SunSailLogic } from "@biz/items/sunSail/logic/SunSailLogic";
import { getChecked } from "@biz/logic/getChecked";
import { PD } from "@biz/PD";
import { ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";

export interface IPoleSwordInfo {
  type: PoleSwordType;
  material: MaterialGroup;
}
export const poleSwordArticles = {
  "00E13000202": { type: "single", material: "alu" },
  "00E13000147": { type: "double", material: "alu" },
  "00E13000123": { type: "single", material: "steel" },
  "00E13000148": { type: "double", material: "steel" }
} satisfies Record<string, IPoleSwordInfo>;
export type PoleSwordArticle = keyof typeof poleSwordArticles;

export interface IPoleAmplifierInfo {
  type: "none" | "strengthen" | "bracing";
  material: MaterialGroup | MaterialGroup[];
  premium?: boolean;
  double?: boolean;
}
export const poleAmplifierArticles = {
  "00E13000131": { type: "strengthen", material: "alu" },
  "00E13000096": { type: "strengthen", material: "steel" },
  "00E13001645": { type: "strengthen", material: "steel", premium: true },
  "00E13000765": { type: "bracing", material: ["telescope", "profile"] },
  "00E13001897": { type: "bracing", material: "alu" },
  "00E13001899": { type: "bracing", material: "alu", double: true },
  "00E13001898": { type: "bracing", material: "steel" },
  "00E13001900": { type: "bracing", material: "steel", double: true },
  "__self_responsibility__": { type: "none", material: [] }
} satisfies Record<string, IPoleAmplifierInfo>;
export type PoleAmplifierArticle = keyof typeof poleAmplifierArticles;

export class AmplifierLogic {
  static calcPoleHeightMountPos(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const bottomExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex)
    const topY = SunSailLogic.calcTopMountY(sunSail, poleIndex);
    return poleLength - bottomExtension - topY;
  }

  static getPoleSwordArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): PoleSwordArticle | undefined {
    return getChecked({
      selection: Poles.getPole(sunSail, poleIndex).articleSlots?.poleSword,
      validItems: AmplifierLogic.calcPossiblePoleSwordArticles(sunSail, poleIndex),
      emptySelection: undefined
    });
  }

  static calcPossiblePoleSwordArticles(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): PoleSwordArticle[] {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex);
    if (poleType !== PoleType.Pole)
      return [];
    if (SunSailLogic.getPoleLengthB(sunSail, poleIndex))
      return [];
    const heightChange = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex);
    if (SunSailLogic.isSlideBar(heightChange))
      return [];

    const height = AmplifierLogic.calcPoleHeightMountPos(sunSail, poleIndex);
    if (ltc(height, 2.5))
      return [];

    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleIndex);
    switch (materialGroup) {
      case "alu": return ["00E13000202", "00E13000147"];
      case "steel": return ["00E13000123", "00E13000148"];
    }
    return [];
  }

  static calcPossiblePoleStrengthenArticles(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): PoleAmplifierArticle[] {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex);
    if (poleType !== PoleType.Pole)
      return [];

    const ret: PoleAmplifierArticle[] = [];
    const sunSailType = PD.productToSunSailType(sunSail);
    const height = AmplifierLogic.calcPoleHeightMountPos(sunSail, poleIndex);
    if (SST.is(sunSailType, SST.F)) {
      if ((ltc(height, 2) || gec(height, 5))) // Sonnensegel auf Maß ab 30m² => 2m - 5m
        return [];
    }
    if (ltc(height, 1))
      return [];


    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleIndex);
    if (["alu", "steel"].includes(materialGroup || "")) { // nur bei Alu und Edelstahlmasten
      const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
      if (pylonType && ltc(AmplifierLogic.calcPoleAmplifierLength(sunSail, poleIndex), 1.5)) // bei Pylonen, nur wenn genügend Platz => Mastlänge >= 1.5m
        return [];
      if (materialGroup === "alu") {
        ret.push("00E13000131");
      } else if (materialGroup === "steel") {
        const lengthB = SunSailLogic.getPoleLengthB(sunSail, poleIndex);
        if (!lengthB) // bei Edelstahl Biegung geht nur Premium Edelstahl
          ret.push("00E13000096");
        ret.push("00E13001645");
      }
    }
    return ret;
  }

  static calcPossiblePoleBracingArticles(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): PoleAmplifierArticle[] {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex);
    if (poleType !== PoleType.Pole)
      return [];

    const sunSailType = PD.productToSunSailType(sunSail);
    if (sunSailType === SunSailType.SolidayTwin && PoleLogic.isAxle(sunSailType, poleIndex)) {
      return []; // Soliday Twin: A, B => keine Abspannung
    }

    const height = AmplifierLogic.calcPoleHeightMountPos(sunSail, poleIndex);
    if (ltc(height, 1))
      return [];

    const ret: PoleAmplifierArticle[] = [];

    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleIndex);
    if (["alu", "steel"].includes(materialGroup || "")) {
      if (materialGroup === "alu") {
        return ["00E13001897", "00E13001899"]
      } else if (materialGroup === "steel") {
        return ["00E13001898", "00E13001900"]
      }
    } else if (["tele", "profile"].includes(materialGroup || "")) {
      return ["00E13000765"];
    }

    return ret;
  }

  static isNoneAvailable(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): boolean {
    const height = AmplifierLogic.calcPoleHeightMountPos(sunSail, poleIndex);

    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.CsXsTwin)) {
      if (PoleLogic.isAxle(sunSailType, poleIndex))
        return ltc(height, 2); // Soliday Twin, CS, XS: A, B => bis 2m
      return ltc(height, 4);
    } else if (SST.is(sunSailType, SST.One)) {
      if (poleIndex === PoleIndex.A || SunSailLogic.getOperatingDeflectionPosition(sunSail) === poleIndex) {
        return ltc(height, 2.5) // Soliday One: A und Umlenkpunkt bis 2.5m
      }
    } else if (SST.is(sunSailType, SST.A, SST.X)) {
      const area = SunSailLogic.calcSunSailAreaCloth(sunSail)
      if (area >= 35)
        return ltc(height, 3.5)
    } else if (SST.is(sunSailType, SST.M)) {
      const area = SunSailLogic.calcSunSailAreaCloth(sunSail)
      if (PoleLogic.isAxle(sunSailType, poleIndex)) {
        return area >= 35 ? ltc(height, 2.5) : ltc(height, 3.0);
      } else {
        if (area >= 35) {
          return ltc(height, 3.5)
        }
      }
    } else if (SST.is(sunSailType, SST.C)) {
    } else if (SST.is(sunSailType, SST.Flex)) {
      if (PoleLogic.isAxle(sunSailType, poleIndex))
        return ltc(height, 2.5);
      else if (!PoleLogic.isAxle(sunSailType, poleIndex))
        return ltc(height, 3);
    }

    return ltc(height, 4);
  }

  static calcPossibleAmplifierComboItems(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): (PoleAmplifierArticle | undefined)[] {
    const validItems: (PoleAmplifierArticle | undefined)[] = [
      ...AmplifierLogic.calcPossiblePoleStrengthenArticles(sunSail, poleIndex),
      ...AmplifierLogic.calcPossiblePoleBracingArticles(sunSail, poleIndex)
    ]
    const hasNone = validItems.length === 0 || AmplifierLogic.isNoneAvailable(sunSail, poleIndex);
    if (hasNone)
      validItems.unshift(undefined);
    else
      validItems.push("__self_responsibility__")
    return validItems;
  }

  static getAmplifierArticleWithSelfResponsibility(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): PoleAmplifierArticle | undefined {
    const validItems = AmplifierLogic.calcPossibleAmplifierComboItems(sunSail, poleIndex);
    return getChecked({
      selection: Poles.getPole(sunSail, poleIndex).articleSlots?.amplifier,
      validItems,
      emptySelection: "__selectionRequired__"
    });
  }

  static getAmplifierArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): Exclude<PoleAmplifierArticle, "__self_responsibility__"> | undefined {
    const amplifier = AmplifierLogic.getAmplifierArticleWithSelfResponsibility(sunSail, poleIndex);
    if (amplifier === "__self_responsibility__")
      return undefined;
    return amplifier;
  }

  static calcPoleAmplifierLength(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
    const pylonInfo = pylonType ? SunSailLogic.getPylonInfo(pylonType) : undefined;
    const spaceAvailable = poleLength - (pylonInfo ? pylonInfo.length : 0);
    const length = MathUtil.clamp(spaceAvailable, 0, 3);
    return length;
  }

  static calcPoleBracing(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): { count: number; length: number; userLength: number | undefined } | undefined {
    const amplifierArticle = AmplifierLogic.getAmplifierArticle(sunSail, poleIndex);
    if (!amplifierArticle) return undefined;
    const info: IPoleAmplifierInfo | undefined = poleAmplifierArticles[amplifierArticle];
    if (info?.type !== "bracing")
      return undefined;

    const userLength = Poles.getPole(sunSail, poleIndex).articleSlots?.amplifierRopeLength;
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const bottomExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex);
    const autoValue = (poleLength - bottomExtension) / Math.cos(Math.PI / 4)
    return {
      length: userLength !== undefined ? userLength : MathUtil.clamp(Math.ceil(autoValue), 0, 100),
      count: info.double ? 2 : 1,
      userLength
    }
  }
}